import React from "react";
import { Box, Typography, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import GradientButton from "../common/GradiantButton";
import SecondaryButton from "../common/SecondaryButton";

/**
 * Home hero — Swiper of Admin home_banners when present, else banner_image, else gradient.
 */
const HomeHero = ({ bannerSlides = [] }) => {
  const navigate = useNavigate();
  const theme = useSelector((s) => s.themeValue?.colors) || {};
  const start = theme.primary_gradient_start_color || "#FF2D95";
  const end = theme.primary_gradient_end_color || "#7B1FA2";
  const hasImage = bannerSlides.length > 0;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: { xs: 420, sm: 480, md: 520 },
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
        bgcolor: hasImage ? "#1a1220" : undefined,
        background: hasImage
          ? undefined
          : `linear-gradient(135deg, ${start}18 0%, ${end}22 50%, #f7f5f8 100%)`,
      }}
    >
      {hasImage && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            "& .swiper, & .swiper-wrapper, & .swiper-slide": {
              width: "100%",
              height: "100%",
            },
            "& .swiper-pagination-bullet": { bgcolor: "#fff", opacity: 0.5 },
            "& .swiper-pagination-bullet-active": {
              background: `linear-gradient(90deg, ${start}, ${end})`,
              opacity: 1,
            },
          }}
        >
          <Swiper
            modules={[Autoplay, Pagination]}
            slidesPerView={1}
            loop={bannerSlides.length > 1}
            autoplay={
              bannerSlides.length > 1
                ? { delay: 5000, disableOnInteraction: false }
                : false
            }
            pagination={bannerSlides.length > 1 ? { clickable: true } : false}
            style={{ width: "100%", height: "100%" }}
          >
            {bannerSlides.map((src, idx) => (
              <SwiperSlide key={`${src}-${idx}`}>
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    backgroundImage: `url(${src})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center center",
                    backgroundRepeat: "no-repeat",
                  }}
                />
              </SwiperSlide>
            ))}
          </Swiper>
          {/* Readability overlay — soft, not neon */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(90deg, rgba(20,12,28,0.72) 0%, rgba(20,12,28,0.45) 55%, rgba(20,12,28,0.25) 100%)",
              pointerEvents: "none",
            }}
          />
        </Box>
      )}

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 2.5, sm: 4, md: 5 },
          py: { xs: 5, sm: 6 },
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: 12, sm: 13 },
            fontWeight: 700,
            letterSpacing: 1.6,
            textTransform: "uppercase",
            color: hasImage ? "rgba(255,255,255,0.9)" : start,
            mb: 1,
          }}
        >
          E &amp; J GLOBAL REALTORS
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: 11, sm: 12 },
            fontWeight: 600,
            letterSpacing: 1.2,
            textTransform: "uppercase",
            color: hasImage ? "rgba(255,255,255,0.75)" : "text.secondary",
            mb: { xs: 2, sm: 2.5 },
          }}
        >
          YOUR DREAM | OUR COMMITMENT | A BETTER TOMORROW
        </Typography>

        <Typography
          component="h1"
          sx={{
            fontWeight: 800,
            fontSize: { xs: "1.75rem", sm: "2.35rem", md: "2.75rem" },
            lineHeight: 1.2,
            color: hasImage ? "#fff" : theme.text || "#1a1220",
            maxWidth: 640,
            mb: 1.5,
          }}
        >
          Turning Your Real Estate Goals into{" "}
          <Box
            component="span"
            sx={{
              background: `linear-gradient(90deg, ${start}, ${end})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Reality
          </Box>
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: "1.05rem", sm: "1.2rem" },
            fontWeight: 600,
            fontStyle: "italic",
            color: hasImage ? "rgba(255,255,255,0.92)" : end,
            mb: 1.25,
            maxWidth: 520,
          }}
        >
          Invest Today for a Better Tomorrow
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: 13, sm: 14 },
            fontWeight: 600,
            letterSpacing: 0.8,
            textTransform: "uppercase",
            color: hasImage ? "rgba(255,255,255,0.8)" : "text.secondary",
            mb: 3,
          }}
        >
          Building Spaces. Creating Brighter Futures.
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{ maxWidth: { sm: 440 } }}
        >
          <GradientButton
            text="Explore Properties"
            onClick={() => navigate("/properties")}
            width={{ xs: "100%", sm: 200 }}
            height={46}
          />
          <SecondaryButton
            text="Contact Us"
            onClick={() => navigate("/contact")}
            width={{ xs: "100%", sm: 160 }}
            height={46}
            sx={{
              ...(hasImage
                ? {
                    borderColor: "#fff",
                    color: "#fff",
                    backgroundColor: "transparent",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.12)",
                      borderColor: "#fff",
                      color: "#fff",
                      "& .MuiTypography-root": { color: "#fff" },
                    },
                    "& .MuiTypography-root": { color: "#fff" },
                  }
                : {}),
            }}
          />
        </Stack>
      </Box>
    </Box>
  );
};

export default HomeHero;
